// Add this at the top of the file to extend the Window interface
declare global {
  interface Window {
    __darvin_lastMouseEvent?: MouseEvent;
  }
}

import { useEffect, useRef } from "react";
import {
  LuLogOut,
  LuMenu,
  LuMonitor,
  LuMoon,
  LuPlus,
  LuSearch,
  LuSettings,
  LuSun,
} from "react-icons/lu";
import { useAuthUser } from "../../core_stores/useAuthStore";
import { Theme, useTheme } from "../../providers/ThemeProvider";
import {
  SideMenuBehavior,
  useCanReopenSideMenu,
  useSideMenuActions,
  useSideMenuBehavior,
} from "../../view_stores/useSideMenuStore";

export default function SideMenuPanel() {
  const { open: openSideMenu, setCanReopenMenu } = useSideMenuActions();
  const sideMenuBehavior = useSideMenuBehavior();
  const canReopenMenu = useCanReopenSideMenu();
  const panelRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Listen for custom event to block reopening after close
  useEffect(() => {
    function handleBlockReopen() {
      // If cursor is over the panel, block reopening
      if (panelRef.current) {
        const rect = panelRef.current.getBoundingClientRect();
        const { clientX, clientY } = window.__darvin_lastMouseEvent || {};
        if (
          typeof clientX === "number" &&
          typeof clientY === "number" &&
          clientX >= rect.left &&
          clientX <= rect.right &&
          clientY >= rect.top &&
          clientY <= rect.bottom
        ) {
          setCanReopenMenu(false);
        } else if (sideMenuBehavior === SideMenuBehavior.PINNED) {
          setCanReopenMenu(false);
        }
      }
    }
    window.addEventListener("darvin-block-reopen-menu", handleBlockReopen);
    return () => {
      window.removeEventListener("darvin-block-reopen-menu", handleBlockReopen);
    };
  }, [sideMenuBehavior]);

  // Track last mouse position globally
  useEffect(() => {
    function handleMouseMove(e: MouseEvent) {
      window.__darvin_lastMouseEvent = e;
    }
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <div
      ref={panelRef}
      onMouseEnter={() => {
        if (canReopenMenu) {
          // Clear any existing timeout
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }

          timeoutRef.current = setTimeout(() => {
            openSideMenu();
          }, 100);
        }
      }}
      onMouseLeave={() => {
        // Cancel the timeout if mouse leaves
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setCanReopenMenu(true);
      }}
      className={`${
        sideMenuBehavior === SideMenuBehavior.PINNED && "hidden"
      } h-full w-[60px] flex flex-col 
      justify-between items-center py-4 gap-8
      fixed left-0 overflow-hidden
        bg-light-surface-secondary dark:bg-dark-surface-secondary 
        transform transition-transform duration-300 ease-in-out z-40 
        shadow-[5px_0_6px_0_rgba(0,0,0,0.25)]`}
    >
      <div className="w-full flex flex-col gap-[25px] items-center">
        <LuMenu
          size={34}
          className="cursor-pointer text-light-text dark:text-dark-text"
          onClick={openSideMenu}
        />
        <div className="p-2 bg-primary-blue rounded-full flex items-center justify-center text-white">
          <LuPlus size={24} />
        </div>
        <LuSearch
          size={30}
          className="text-light-text dark:text-dark-text mt-1"
        />
      </div>
      <div
        className="w-full flex flex-col items-center gap-[16px] pb-[84px]
      text-light-text dark:text-dark-text"
      >
        {/* <ThemeIcon /> */}
        <LuSettings size={30} className="text-[#8B8B8B]" />
        <Avatar />
        <LuLogOut size={30} className="text-[#8B8B8B]" />
      </div>
    </div>
  );
}

function ThemeIcon() {
  const { theme } = useTheme();

  return (
    <div
      className="text-[#8B8B8B] pb-1
        rounded-full transition-transform duration-500 ease-in-out"
    >
      {theme === Theme.LIGHT ? (
        <LuSun size={30} />
      ) : theme === Theme.DARK ? (
        <LuMoon size={30} />
      ) : (
        <LuMonitor size={30} />
      )}
    </div>
  );
}

function Avatar() {
  const user = useAuthUser();

  return (
    <div className="my-[9px] w-[34px] h-[34px] rounded-full overflow-hidden">
      <img src={user?.photoURL || ""} />
    </div>
  );
}
